import { Box, Flex, Select as SelectUI, Text } from '@radix-ui/themes'
import * as React from 'react'
import { useTranslation } from 'react-i18next'

import { Image } from '#app/components/ui/image.tsx'
import { Label } from '#app/components/ui/label/label.tsx'
import { Select } from '#app/components/ui/select'

/* -------------------------------------------------------------------------------------------------
 * I18next
 * -----------------------------------------------------------------------------------------------*/

enum I18NEXT {
	uiSelectCountryCode = 'ui-select-country-code',
}

/* -------------------------------------------------------------------------------------------------
 * SelectCountryCode
 * -----------------------------------------------------------------------------------------------*/

type SelectCountryCodeElement = React.ElementRef<typeof Select>
type SelectCountryCodeProps = React.ComponentPropsWithoutRef<typeof Select>

const SelectCountryCode = React.forwardRef<
	SelectCountryCodeElement,
	SelectCountryCodeProps
>((props, forwardedRef) => {
	const { t } = useTranslation(I18NEXT.uiSelectCountryCode)
	const list = React.useMemo(
		() => countries.filter(({ alpha_2 }) => alpha_2 === 'DE'),
		[],
	)

	return (
		<Flex direction={{ initial: 'column', lg: 'row' }} gap="2" align="baseline">
			<Box className="order-2 lg:order-1">
				<Select
					{...props}
					ref={forwardedRef}
					placeholder={t('button.selectACountry')}
					t={t}
					Label={
						<Label
							htmlFor={props.name}
							className="-ml-10 select-none text-gray-9 xs:order-1 xs:ml-0"
						>
							{t('label.countryCode')}
						</Label>
					}
				>
					<SelectUI.Group>
						{list.map(({ alpha_2, label, flag }) => (
							<SelectUI.Item
								id={`${props.meta.id}-${alpha_2}`}
								key={alpha_2}
								value={alpha_2}
							>
								<Flex align="center" gapX="2">
									<Image
										api={false}
										src={`/pwa/flags/${flag}`}
										alt={label}
										width={26}
										height={26}
									/>
									<Text>{t(`label.${label}`)}</Text>
								</Flex>
							</SelectUI.Item>
						))}
					</SelectUI.Group>
				</Select>
			</Box>
		</Flex>
	)
})
SelectCountryCode.displayName = 'SelectCountryCode'

const countries = [
	{
		alpha_2: 'AF',
		label: 'Afghanistan',
		name: 'Afghanistan',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'AX',
		label: 'Åland Islands',
		name: 'Aland Islands',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'AL',
		label: 'Albania',
		name: '',
		alpha_3: 'Albania',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'DZ',
		label: 'Algeria',
		name: '',
		alpha_3: 'Algeria',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'AS',
		label: 'American Samoa',
		name: 'American Samoa',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'AD',
		label: 'Andorra',
		name: 'Andorra',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'AO',
		label: 'Angola',
		name: 'Angola',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'AI',
		label: 'Anguilla',
		name: 'Anguilla',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'AQ',
		label: 'Antarctica',
		name: 'Antarctica',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'AG',
		label: 'Antigua & Barbuda',
		name: 'Antigua and Barbuda',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'AR',
		label: 'Argentina',
		name: 'Argentina',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'AM',
		label: 'Armenia',
		name: 'Armenia',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'AW',
		label: 'Aruba',
		name: 'Aruba',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'AU',
		label: 'Australia',
		name: 'Australia',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'AT',
		label: 'Austria',
		name: 'Austria',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'AZ',
		label: 'Azerbaijan',
		name: 'Azerbaijan',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'BS',
		label: 'Bahamas',
		name: 'Bahamas',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'BH',
		label: 'Bahrain',
		name: 'Bahrain',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'BD',
		label: 'Bangladesh',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'BB',
		label: 'Barbados',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'BY',
		label: 'Belarus',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'BE',
		label: 'Belgium',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'BZ',
		label: 'Belize',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'BJ',
		label: 'Benin',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'BM',
		label: 'Bermuda',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'BT',
		label: 'Bhutan',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'BO',
		label: 'Bolivia',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'BQ',
		label: 'Caribbean Netherlands',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'BA',
		label: 'Bosnia & Herzegovina',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'BW',
		label: 'Botswana',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'BV',
		label: 'Bouvet Island',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'BR',
		label: 'Brazil',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'IO',
		label: 'British Indian Ocean Territory',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'BN',
		label: 'Brunei',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'BG',
		label: 'Bulgaria',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'BF',
		label: 'Burkina Faso',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'BI',
		label: 'Burundi',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'KH',
		label: 'Cambodia',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'CM',
		label: 'Cameroon',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'CA',
		label: 'Canada',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'CV',
		label: 'Cape Verde',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'KY',
		label: 'Cayman Islands',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'CF',
		label: 'Central African Republic',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'TD',
		label: 'Chad',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'CL',
		label: 'Chile',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'CN',
		label: 'China',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'CX',
		label: 'Christmas Island',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'CC',
		label: 'Cocos (Keeling) Islands',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'CO',
		label: 'Colombia',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'KM',
		label: 'Comoros',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'CG',
		label: 'Congo - Brazzaville',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'CD',
		label: 'Congo - Kinshasa',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'CK',
		label: 'Cook Islands',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'CR',
		label: 'Costa Rica',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'CI',
		label: 'Côte d’Ivoire',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'HR',
		label: 'Croatia',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'CU',
		label: 'Cuba',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'CW',
		label: 'Curaçao',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'CY',
		label: 'Cyprus',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'CZ',
		label: 'Czechia',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'DK',
		label: 'Denmark',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'DJ',
		label: 'Djibouti',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'DM',
		label: 'Dominica',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'DO',
		label: 'Dominican Republic',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'EC',
		label: 'Ecuador',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'EG',
		label: 'Egypt',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'SV',
		label: 'El Salvador',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'GQ',
		label: 'Equatorial Guinea',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'ER',
		label: 'Eritrea',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'EE',
		label: 'Estonia',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'ET',
		label: 'Ethiopia',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'FK',
		label: 'Falkland Islands (Islas Malvinas)',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'FO',
		label: 'Faroe Islands',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'FJ',
		label: 'Fiji',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'FI',
		label: 'Finland',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'FR',
		label: 'France',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'GF',
		label: 'French Guiana',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'PF',
		label: 'French Polynesia',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'TF',
		label: 'French Southern Territories',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'GA',
		label: 'Gabon',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'GM',
		label: 'Gambia',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'GE',
		label: 'Georgia',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'DE',
		label: 'germany',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: 'de.svg',
	},
	{
		alpha_2: 'GH',
		label: 'Ghana',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'GI',
		label: 'Gibraltar',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'GR',
		label: 'Greece',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'GL',
		label: 'Greenland',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'GD',
		label: 'Grenada',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'GP',
		label: 'Guadeloupe',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'GU',
		label: 'Guam',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'GT',
		label: 'Guatemala',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'GG',
		label: 'Guernsey',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'GN',
		label: 'Guinea',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'GW',
		label: 'Guinea-Bissau',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'GY',
		label: 'Guyana',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'HT',
		label: 'Haiti',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'HM',
		label: 'Heard & McDonald Islands',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'VA',
		label: 'Vatican City',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'HN',
		label: 'Honduras',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'HK',
		label: 'Hong Kong',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'HU',
		label: 'Hungary',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'IS',
		label: 'Iceland',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'IN',
		label: 'India',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'ID',
		label: 'Indonesia',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'IR',
		label: 'Iran',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'IQ',
		label: 'Iraq',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'IE',
		label: 'Ireland',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'IM',
		label: 'Isle of Man',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'IL',
		label: 'Israel',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'IT',
		label: 'Italy',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'JM',
		label: 'Jamaica',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'JP',
		label: 'Japan',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'JE',
		label: 'Jersey',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'JO',
		label: 'Jordan',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'KZ',
		label: 'Kazakhstan',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'KE',
		label: 'Kenya',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'KI',
		label: 'Kiribati',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'KP',
		label: 'North Korea',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'KR',
		label: 'South Korea',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'XK',
		label: 'Kosovo',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'KW',
		label: 'Kuwait',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'KG',
		label: 'Kyrgyzstan',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'LA',
		label: 'Laos',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'LV',
		label: 'Latvia',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'LB',
		label: 'Lebanon',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'LS',
		label: 'Lesotho',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'LR',
		label: 'Liberia',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'LY',
		label: 'Libya',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'LI',
		label: 'Liechtenstein',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'LT',
		label: 'Lithuania',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'LU',
		label: 'Luxembourg',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'MO',
		label: 'Macao',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'MK',
		label: 'North Macedonia',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'MG',
		label: 'Madagascar',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'MW',
		label: 'Malawi',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'MY',
		label: 'Malaysia',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'MV',
		label: 'Maldives',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'ML',
		label: 'Mali',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'MT',
		label: 'Malta',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'MH',
		label: 'Marshall Islands',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'MQ',
		label: 'Martinique',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'MR',
		label: 'Mauritania',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'MU',
		label: 'Mauritius',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'YT',
		label: 'Mayotte',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'MX',
		label: 'Mexico',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'FM',
		label: 'Micronesia',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'MD',
		label: 'Moldova',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'MC',
		label: 'Monaco',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'MN',
		label: 'Mongolia',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'ME',
		label: 'Montenegro',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'MS',
		label: 'Montserrat',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'MA',
		label: 'Morocco',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'MZ',
		label: 'Mozambique',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'MM',
		label: 'Myanmar (Burma)',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'NA',
		label: 'Namibia',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'NR',
		label: 'Nauru',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'NP',
		label: 'Nepal',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'NL',
		label: 'Netherlands',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'AN',
		label: 'Curaçao',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'NC',
		label: 'New Caledonia',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'NZ',
		label: 'New Zealand',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'NI',
		label: 'Nicaragua',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'NE',
		label: 'Niger',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'NG',
		label: 'Nigeria',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'NU',
		label: 'Niue',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'NF',
		label: 'Norfolk Island',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'MP',
		label: 'Northern Mariana Islands',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'NO',
		label: 'Norway',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'OM',
		label: 'Oman',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'PK',
		label: 'Pakistan',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'PW',
		label: 'Palau',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'PS',
		label: 'Palestine',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'PA',
		label: 'Panama',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'PG',
		label: 'Papua New Guinea',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'PY',
		label: 'Paraguay',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'PE',
		label: 'Peru',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'PH',
		label: 'Philippines',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'PN',
		label: 'Pitcairn Islands',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'PL',
		label: 'Poland',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'PT',
		label: 'Portugal',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'PR',
		label: 'Puerto Rico',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'QA',
		label: 'Qatar',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'RE',
		label: 'Réunion',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'RO',
		label: 'Romania',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'RU',
		label: 'Russia',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'RW',
		label: 'Rwanda',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'BL',
		label: 'St. Barthélemy',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'SH',
		label: 'St. Helena',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'KN',
		label: 'St. Kitts & Nevis',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'LC',
		label: 'St. Lucia',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'MF',
		label: 'St. Martin',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'PM',
		label: 'St. Pierre & Miquelon',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'VC',
		label: '',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'St. Vincent & Grenadines',
		label: '',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'WS',
		label: 'Samoa',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'SM',
		label: 'San Marino',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'ST',
		label: 'São Tomé & Príncipe',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'SA',
		label: 'Saudi Arabia',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'SN',
		label: 'Senegal',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'RS',
		label: 'Serbia',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'CS',
		label: 'Serbia',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'SC',
		label: 'Seychelles',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'SL',
		label: 'Sierra Leone',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'SG',
		label: 'Singapore',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'SX',
		label: 'Sint Maarten',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'SK',
		label: 'Slovakia',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'SI',
		label: 'Slovenia',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'SB',
		label: 'Solomon Islands',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'SO',
		label: 'Somalia',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'ZA',
		label: 'South Africa',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'GS',
		label: 'South Georgia & South Sandwich Islands',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'SS',
		label: 'South Sudan',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'ES',
		label: 'Spain',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'LK',
		label: 'Sri Lanka',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'SD',
		label: 'Sudan',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'SR',
		label: 'Suriname',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'SJ',
		label: 'Svalbard & Jan Mayen',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'SZ',
		label: 'Eswatini',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'SE',
		label: 'Sweden',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'CH',
		label: 'Switzerland',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'SY',
		label: 'Syria',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'TW',
		label: 'Taiwan',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'TJ',
		label: 'Tajikistan',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'TZ',
		label: 'Tanzania',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'TH',
		label: 'Thailand',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'TL',
		label: 'Timor-Leste',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'TG',
		label: 'Togo',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'TK',
		label: 'Tokelau',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'TO',
		label: 'Tonga',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'TT',
		label: 'Trinidad & Tobago',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'TN',
		label: 'Tunisia',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'TR',
		label: 'Turkey',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'TM',
		label: 'Turkmenistan',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'TC',
		label: 'Turks & Caicos Islands',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'TV',
		label: 'Tuvalu',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'UG',
		label: 'Uganda',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'UA',
		label: 'Ukraine',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'AE',
		label: 'United Arab Emirates',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'GB',
		label: 'United Kingdom',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'US',
		label: 'United States',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'UM',
		label: 'U.S. Outlying Islands',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'UY',
		label: 'Uruguay',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'UZ',
		label: 'Uzbekistan',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'VU',
		label: 'Vanuatu',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'VE',
		label: 'Venezuela',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'VN',
		label: 'Vietnam',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'VG',
		label: 'British Virgin Islands',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'VI',
		label: 'U.S. Virgin Islands',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'WF',
		label: 'Wallis & Futuna',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'EH',
		label: 'Western Sahara',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'YE',
		label: 'Yemen',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'ZM',
		label: 'Zambia',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
	{
		alpha_2: 'ZW',
		label: 'Zimbabwe',
		name: '',
		alpha_3: '',
		phone: '',
		continent: '',
		capital: '',
		currency: '',
		currency_symbol: '',
		continent_code: '',
		flag: '',
	},
]

/* -----------------------------------------------------------------------------------------------*/

export { SelectCountryCode, I18NEXT as SELECT_COUNTRY_CODE_I18NEXT }
